import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { api, call } from './client';
import { qk } from './keys';
import type { CustomField, MemberRole, Project, ProjectDetails, ProjectStatus, Section } from './types';

export function useProjects(includeArchived = false) {
  return useQuery({
    queryKey: qk.projects(includeArchived),
    queryFn: async () => (await call(api.GET('/api/v1/projects', { params: { query: { includeArchived } } }))).data,
    staleTime: 60_000,
  });
}

export function useProjectMap(): Map<string, Project> {
  const { data } = useProjects(true);
  return useMemo(() => new Map((data ?? []).map((p) => [p.id, p])), [data]);
}

export function projectQueryOptions(id: string) {
  return {
    queryKey: qk.project(id),
    queryFn: () => call(api.GET('/api/v1/projects/{id}', { params: { path: { id } } })),
    staleTime: 60_000,
  };
}

export function useProject(id: string | undefined) {
  return useQuery({ ...projectQueryOptions(id ?? ''), enabled: !!id });
}

/** Деталі кількох проєктів (етапи й поля) — для назв етапів у переліках задач. */
export function useProjectDetailsMany(ids: string[]): Map<string, ProjectDetails> {
  const key = [...new Set(ids)].sort().join(',');
  const unique = useMemo(() => (key ? key.split(',') : []), [key]);
  const combine = useCallback(
    (results: { data?: ProjectDetails }[]) => {
      const m = new Map<string, ProjectDetails>();
      results.forEach((r, i) => {
        if (r.data) m.set(unique[i]!, r.data);
      });
      return m;
    },
    [unique],
  );
  return useQueries({ queries: unique.map((id) => projectQueryOptions(id)), combine });
}

/** Карта етапів за ідентифікатором із наявних деталей проєктів. */
export function sectionIndex(details: Iterable<ProjectDetails>): Map<string, Section> {
  const m = new Map<string, Section>();
  for (const p of details) for (const s of p.sections) m.set(s.id, s);
  return m;
}

export function useProjectTemplates() {
  return useQuery({
    queryKey: qk.projectTemplates,
    queryFn: async () => (await call(api.GET('/api/v1/project-templates'))).data,
    staleTime: Infinity,
  });
}

export function useCustomFieldLibrary() {
  return useQuery({
    queryKey: qk.customFields,
    queryFn: async () => (await call(api.GET('/api/v1/custom-fields'))).data,
    staleTime: 5 * 60_000,
  });
}

export function useTags() {
  return useQuery({
    queryKey: qk.tags,
    queryFn: async () => (await call(api.GET('/api/v1/tags'))).data,
    staleTime: 5 * 60_000,
  });
}

export function useCreateTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; color?: string }) => call(api.POST('/api/v1/tags', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.tags }),
  });
}

type SectionBody = {
  name: string;
  wipLimit?: number | null;
  slaHours?: number | null;
  requiredFieldIds?: string[];
  allowNoDueDate?: boolean;
  isDoneStage?: boolean;
};

export type CreateProjectBody = {
  name: string;
  description?: string;
  type?: Project['type'];
  visibility?: Project['visibility'];
  teamId?: string;
  processCode?: string;
  defaultView?: Project['defaultView'];
  templateKey?: string;
  sections?: SectionBody[];
  memberIds?: string[];
  startOn?: string;
  dueOn?: string;
};

function setDetails(qc: ReturnType<typeof useQueryClient>, d: ProjectDetails) {
  qc.setQueryData(qk.project(d.id), d);
  void qc.invalidateQueries({ queryKey: qk.projectsAll });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProjectBody) => call(api.POST('/api/v1/projects', { body })),
    onSuccess: (d) => setDetails(qc, d),
  });
}

export type PatchProjectBody = {
  name?: string;
  description?: string | null;
  visibility?: Project['visibility'];
  processCode?: string | null;
  defaultView?: Project['defaultView'];
  ownerId?: string | null;
  startOn?: string | null;
  dueOn?: string | null;
  status?: ProjectStatus | null;
};

export function usePatchProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchProjectBody }) =>
      call(api.PATCH('/api/v1/projects/{id}', { params: { path: { id } }, body })),
    onSuccess: (d) => setDetails(qc, d),
  });
}

export function useArchiveProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => call(api.DELETE('/api/v1/projects/{id}', { params: { path: { id } } })),
    onSuccess: (d) => setDetails(qc, d),
  });
}

export function useAddSection(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SectionBody & { afterSectionId?: string }) =>
      call(api.POST('/api/v1/projects/{id}/sections', { params: { path: { id: projectId } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.project(projectId) }),
  });
}

export function usePatchSection(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<SectionBody> & { position?: number } }) =>
      call(api.PATCH('/api/v1/sections/{id}', { params: { path: { id } }, body })),
    onSuccess: (s) => {
      qc.setQueryData<ProjectDetails>(qk.project(projectId), (d) =>
        d ? { ...d, sections: d.sections.map((x) => (x.id === s.id ? s : x)).sort((a, b) => a.position - b.position) } : d,
      );
      void qc.invalidateQueries({ queryKey: qk.project(projectId) });
    },
  });
}

export function useDeleteSection(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, moveTasksTo }: { id: string; moveTasksTo?: string }) =>
      call(api.DELETE('/api/v1/sections/{id}', { params: { path: { id }, query: moveTasksTo ? { moveTasksTo } : {} } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.project(projectId) });
      void qc.invalidateQueries({ queryKey: qk.tasksAll });
    },
  });
}

export function useSetMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: MemberRole }) =>
      call(api.PUT('/api/v1/projects/{id}/members/{userId}', { params: { path: { id: projectId, userId } }, body: { role } })),
    onSuccess: (d) => setDetails(qc, d),
  });
}

export function useRemoveMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      call(api.DELETE('/api/v1/projects/{id}/members/{userId}', { params: { path: { id: projectId, userId } } })),
    onSuccess: (d) => setDetails(qc, d),
  });
}

export function useAttachField(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldId, required, position }: { fieldId: string; required?: boolean; position?: number }) =>
      call(api.PUT('/api/v1/projects/{id}/custom-fields/{fieldId}', { params: { path: { id: projectId, fieldId } }, body: { required, position } })),
    onSuccess: (d) => setDetails(qc, d),
  });
}

export function useDetachField(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fieldId: string) =>
      call(api.DELETE('/api/v1/projects/{id}/custom-fields/{fieldId}', { params: { path: { id: projectId, fieldId } } })),
    onSuccess: (d) => setDetails(qc, d),
  });
}

export type FieldBody = {
  name: string;
  description?: string | null;
  type: CustomField['type'];
  options?: { id?: string; name: string; color?: string; archived?: boolean }[];
  currency?: string | null;
  precision?: number | null;
  adminOnly?: boolean;
  sensitive?: boolean;
};

export function useCreateField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FieldBody) => call(api.POST('/api/v1/custom-fields', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.customFields }),
  });
}

export const PROJECT_TYPE_LABELS: Record<Project['type'], string> = {
  process: 'Процесний потік',
  project: 'Проєкт зі строком',
  reference: 'Довідник',
  personal: 'Особистий',
};

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  on_track: 'У графіку',
  at_risk: 'Під загрозою',
  off_track: 'Відстає',
  on_hold: 'Призупинено',
  complete: 'Завершено',
};

export const MEMBER_ROLE_LABELS: Record<MemberRole, string> = {
  admin: 'Адміністратор',
  editor: 'Редактор',
  commenter: 'Коментатор',
  viewer: 'Читач',
};

export const VIEW_LABELS: Record<Project['defaultView'], string> = {
  list: 'Список',
  board: 'Дошка',
  calendar: 'Календар',
  timeline: 'Хронологія',
};

export const FIELD_TYPE_LABELS: Record<CustomField['type'], string> = {
  text: 'Текст',
  number: 'Число',
  currency: 'Сума',
  percent: 'Відсоток',
  date: 'Дата',
  datetime: 'Дата й час',
  enum: 'Один варіант',
  multi_enum: 'Кілька варіантів',
  people: 'Люди',
  boolean: 'Так / ні',
  url: 'Посилання',
  email: 'Електронна пошта',
  phone: 'Телефон',
  duration: 'Тривалість (хв)',
  rating: 'Оцінка 0–5',
};

export function canEditProject(role: MemberRole | null | undefined) {
  return role === 'admin' || role === 'editor';
}
